import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { RootState } from "../../../../globals";
import { accessMode, getAccessPermissions } from "../../../../common";
import useEsTelefono from "../../../../utils/esTelefono";
import AcademicCalendarController from "../academicCalendar/academicCalendarController";
import PeriodosTab from "./periodosTab";
import "../enrollmentRequests/filtrosMatriculas.css";
import "./calendarioAcademico.css";

/*
	Calendario Académico (Horarios Claros, 2026-11-01): Periodos y Asuetos en una sola pantalla. Cada pestaña sale solo si la
	persona tiene su sección en el menú (periods / academicCalendar). Las rutas viejas /periods y /academicCalendar abren aquí,
	en su pestaña.
*/

export type PestanaCalendario = "periodos" | "asuetos";

const PESTANAS: { clave: PestanaCalendario; texto: string; seccion: string }[] = [
	{ clave: "periodos", texto: "Periodos", seccion: "periods" },
	{ clave: "asuetos", texto: "Asuetos", seccion: "academicCalendar" },
];

/** Tiene la sección en su rol actual con permiso de entrar al menú. */
export const veSeccion = (usuario: any, seccion: string): boolean => {
	const permiso = usuario?.roleMap?.[usuario?.currentRole]?.permits?.[seccion];
	return !!permiso && (permiso.binary_quintet_RCUDN_permissions & accessMode.NAVIGATE) > 0;
};

const CalendarioAcademicoController: React.FC<{ inicial?: PestanaCalendario }> = ({ inicial = "periodos" }) => {
	const movil = useEsTelefono();
	const usuario: any = useSelector((s: RootState) => s.userProfile.value);
	const currentUserCampus: any = useSelector((s: RootState) => s.currentUserCampus.value);
	const campusRef: any = useSelector((s: RootState) => s.campusRef.value);
	const campusId: number = (campusRef && currentUserCampus != null && campusRef[currentUserCampus]?.id) || 0;

	const visibles = useMemo(() => PESTANAS.filter((p) => veSeccion(usuario, p.seccion)), [usuario]);
	const [elegida, setElegida] = useState<PestanaCalendario>(inicial);
	const [aviso, setAviso] = useState<string | null>(null);
	const [nuevoPeriodo, setNuevoPeriodo] = useState(0);

	// Si la pestaña pedida no la ve, abre la primera que sí.
	const tab: PestanaCalendario = visibles.some((p) => p.clave === elegida) ? elegida : (visibles[0]?.clave ?? "periodos");
	const puedeCrearPeriodo = !!getAccessPermissions(usuario, "periods").create;

	return (
		<div className="container-fluid waModulo calMod" style={{ textAlign: "left", padding: movil ? "10px 12px 0 12px" : "10px 24px 0 24px" }}>
			<div className="calEncabezado">
				<h4>Calendario Académico</h4>
				{tab === "periodos" && puedeCrearPeriodo ? (
					<button type="button" className="btn btn-primary waBotonGrande" onClick={() => setNuevoPeriodo((n) => n + 1)}>
						<FontAwesomeIcon icon={solid("plus")} />&nbsp;Nuevo Periodo
					</button>
				) : null}
			</div>

			{visibles.length > 1 ? (
				<ul className="nav nav-pills waTabs calPestanas" style={{ gap: 4 }}>
					{visibles.map((p) => (
						<li className="nav-item" key={p.clave}>
							<button type="button" className={"nav-link btn btn-sm " + (tab === p.clave ? "active" : "")} style={{ padding: "4px 12px", fontSize: 15 }}
								onClick={() => { setAviso(null); setElegida(p.clave); }}>{p.texto}</button>
						</li>
					))}
				</ul>
			) : null}

			{aviso ? <div className="alert alert-success" style={{ fontSize: 15, display: "flex", justifyContent: "space-between" }}>{aviso}<button type="button" className="btn-close" style={{ fontSize: 10 }} onClick={() => setAviso(null)} /></div> : null}

			{tab === "periodos" ? <PeriodosTab campusId={campusId} onAviso={setAviso} abrirNuevo={nuevoPeriodo} /> : null}
			{tab === "asuetos" ? <AcademicCalendarController embebido /> : null}
		</div>
	);
};

export default CalendarioAcademicoController;
